import { useEffect } from 'react';

import { Box } from '@mui/material';

import RegularChart from '@/components/Chart/RegularChart';
import type {
  ChartType,
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { CHART_HEIGHT } from '@/features/DataHub/constants/charts';
import {
  useExportPreview,
  useExportSettings
} from '@/features/DataHub/modules/Export/context/exportContexts';
import { ExportTypes } from '@/features/DataHub/modules/Export/types';

import { chartSeriesHasData } from './chartSeriesHasData';

const PreviewChartLayout = ({
  chartSeries,
  chartType,
  error,
  loading,
  options,
  height
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  chartType: ChartType;
  error: boolean;
  loading: boolean;
  options: ExtendedOptions | undefined;
  height?: number;
}) => {
  const { setPreviewHasNoData, setPreviewLoading } = useExportPreview();
  const { exportSettings } = useExportSettings();
  const isImageExport = exportSettings.exportType === ExportTypes.IMAGE;
  const hasChartData = chartSeriesHasData(chartSeries);

  useEffect(() => {
    if (!isImageExport) {
      setPreviewLoading(false);
      setPreviewHasNoData(false);
      return;
    }

    setPreviewLoading(loading);
    setPreviewHasNoData(!loading && !error && !hasChartData);

    return () => {
      setPreviewLoading(false);
      setPreviewHasNoData(false);
    };
  }, [error, hasChartData, isImageExport, loading, setPreviewHasNoData, setPreviewLoading]);

  return (
    <Box sx={{ minHeight: height || CHART_HEIGHT, position: 'relative' }}>
      <RegularChart
        chartSeries={chartSeries}
        chartType={chartType}
        error={error}
        loading={loading}
        options={options}
        showLegend={false}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1
        }}
      />
    </Box>
  );
};

export default PreviewChartLayout;
